import { MAX_GROSS_INCOME, MIN_GROSS_INCOME } from "@component/Factoring/SmallCalculator/data";
import type { LeadFormProps } from "@component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { BasicLeadForm } from "@component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { faBuilding as fasBuilding } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { FormCurrency, FormInput } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import type { FactoringLeadFormFields } from "../types";

const MIN_COMPANY_NAME_LENGTH = 2;
const MAX_COMPANY_NAME_LENGTH = 40;

interface Props extends LeadFormProps {
	withExtraInputs?: boolean;
}

type StepFields = FactoringLeadFormFields;

export class LeadForm extends BasicLeadForm<StepFields, Props> {
	protected renderFields(): ReactNode {
		const { submitState, withExtraInputs } = this.props;

		return (
			<>
				{super.renderFields()}
				<FormInput
					placeholder="Cégnév"
					name="companyName"
					prepend={<FontAwesomeIcon icon={fasBuilding} />}
					validate={this.validCompanyName}
					disabled={submitState !== undefined}
					required
				/>
				{withExtraInputs && (
					<>
						<FormCurrency
							placeholder="Árbevétel"
							name="grossIncome"
							validate={this.validGrossIncome}
							disabled={submitState !== undefined}
							required
						/>
						<FormCurrency
							placeholder="Hitelösszeg"
							name="factoringAmount"
							validate={this.validFactoringAmount}
							disabled={submitState !== undefined}
							required
						/>
					</>
				)}
			</>
		);
	}

	private validCompanyName(_companyName: string): string {
		if (!_companyName) {
			return "A cégnév megadása kötelező!";
		}

		const companyName = _companyName?.trim();

		if (companyName.length < MIN_COMPANY_NAME_LENGTH) {
			return `A cégnév legalább ${MIN_COMPANY_NAME_LENGTH} karakterből kell, hogy álljon!`;
		}

		if (companyName.length > MAX_COMPANY_NAME_LENGTH) {
			return `A cégnév legfeljebb ${MAX_COMPANY_NAME_LENGTH} karakterből állhat!`;
		}
	}

	@autobind
	private validGrossIncome(grossIncome: number): string {
		if (isNaN(grossIncome)) {
			return "Kérjük add meg a céged éves bevételét.";
		}

		if (grossIncome < MIN_GROSS_INCOME) {
			return `Az éves árbevétel összege nem lehet kevesebb, mint ${formatText(
				MIN_GROSS_INCOME / 1_000_000,
				"millió ft",
			)}.`;
		}

		if (grossIncome > MAX_GROSS_INCOME) {
			return `Az éves árbevétel összege nem lehet nagyobb, mint ${formatText(
				MAX_GROSS_INCOME / 1_000_000,
				"millió ft",
			)}.`;
		}
	}

	@autobind
	private validFactoringAmount(factoringAmount: number): string {
		if (!factoringAmount || factoringAmount <= 0) {
			return "Kérjük, add meg az igényelt hitelösszeget!";
		}
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.LeadForm`;
	}
}
